import Button from "@/components/ui/Button";
import Checkbox from "@/components/ui/CheckBox";
import TextField from "@/components/ui/Textfield";
import React, { useState } from "react";
import useOnboardingStore from "../store/onboardingStore";
import useGlobalStore from "@/store/globalStore";
import Dropdown from "@/components/ui/DropDown";

interface Props {
  onNext: () => void;
}

const StepTwoMediaAgencyOnboarding: React.FC<Props> = ({ onNext }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState({
    name: "",
    website: "",
    phone: "",
    industry: "",
    size: "",
    main_email: "",
    logo: null,
    previewLogo: null,
    shipping: {
      address_line_1: "",
      address_line_2: "",
      city: "",
      state: "",
      country: "",
      zip_postal: "",
    },
    billing: {
      address_line_1: "",
      address_line_2: "",
      city: "",
      state: "",
      country: "",
      zip_postal: "",
    },
    isSameAddress: true,
  });

  const createMediaAgency = useOnboardingStore(
    (state) => state.createMediaAgency
  );

  const setError = useGlobalStore((state) => state.setError);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name.includes(".")) {
      const [section, key] = name.split(".");

      setFormData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section as keyof typeof prev],
          [key]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const {
      name,
      website,
      phone,
      industry,
      size,
      main_email,
      logo,
      shipping,
      billing,
      isSameAddress,
    } = formData;

    if (!name.trim()) return setError("Company name is required.");
    if (!main_email.trim()) return setError("Email is required.");
    if (!phone.trim()) return setError("Phone number is required.");
    if (!industry.trim()) return setError("Industry is required.");
    if (!size.trim()) return setError("Company size is required.");

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(main_email)) {
      return setError("Please enter a valid email address.");
    }

    if (phone.length < 8) {
      return setError("Phone number must be at least 8 digits.");
    }

    const requiredShippingFields = [
      shipping.address_line_1,
      shipping.city,
      shipping.state,
      shipping.country,
      shipping.zip_postal,
    ];
    if (requiredShippingFields.some((field) => !field.trim())) {
      return setError("Complete shipping address is required.");
    }

    if (!isSameAddress) {
      const requiredBillingFields = [
        billing.address_line_1,
        billing.city,
        billing.state,
        billing.country,
        billing.zip_postal,
      ];
      if (requiredBillingFields.some((field) => !field.trim())) {
        return setError("Complete billing address is required.");
      }
    }

    const finalBilling = isSameAddress ? { ...shipping } : billing;

    try {
      setIsLoading(true);
      await createMediaAgency({
        name,
        website,
        phone,
        industry,
        size,
        main_email,
        logo,
        shipping,
        billing: finalBilling,
      });

      onNext();
    } catch (err: any) {
      setError(err?.message || "Failed to submit user data.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && (file.type === "image/png" || file.type === "image/jpeg")) {
      setFormData({
        ...formData,
        logo: file,
        previewLogo: URL.createObjectURL(file),
      });
    } else {
      setError("Only PNG or JPEG files are allowed.");
    }
  };

  return (
    <div className="bg-[#F8F8F8] min-h-screen flex flex-col items-center justify-center px-4 py-14">
      <div className="max-w-lg w-full text-center mb-6">
        <h1 className="text-5xl font-bold text-[#EA33C0] mb-4">MediaBridge</h1>
        <h2 className="text-xl font-semibold">
          Great to meet you!
          <br />
          Just a few more details about your company.
        </h2>
        <p className="text-sm text-gray-800 mt-4">
          Your company information will be used to build your team account.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="w-full max-w-xl space-y-5 mt-2">
        <div className="flex items-center font-semibold">
          <div className="mr-5 w-56 text-right">Company Name</div>
          <TextField
            type="text"
            name="name"
            value={formData.name}
            onChange={onChange}
            placeholder="Company Name"
            className="w-full"
          />
        </div>

        <div className="flex items-center font-semibold">
          <div className="mr-5 w-56 text-right">Website</div>
          <TextField
            type="text"
            name="website"
            value={formData.website}
            onChange={onChange}
            placeholder="Website"
            className="w-full"
          />
        </div>

        <div className="flex items-center font-semibold">
          <div className="mr-5 w-56 text-right">Phone</div>
          <TextField
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={onChange}
            placeholder="(123) 456-789"
            className="w-full"
          />
        </div>

        <div className="flex items-center font-semibold">
          <div className="mr-5 w-56 text-right">Industry</div>
          <Dropdown
            options={[
              { title: "Media", value: "media" },
              { title: "Other", value: "Other" },
            ]}
            selected={formData.industry}
            setSelected={(option) => {
              setFormData((prev) => ({
                ...prev,
                industry: option.toString(),
              }));
            }}
            className="w-full"
          />
        </div>

        <div className="flex items-center font-semibold">
          <div className="mr-5 w-56 text-right">Size</div>
          <Dropdown
            options={[
              { title: "1-10", value: "1-10" },
              { title: "10-50", value: "10-50" },
              { title: "50+", value: "50+" },
            ]}
            selected={formData.size}
            setSelected={(option) => {
              setFormData((prev) => ({
                ...prev,
                size: option.toString(),
              }));
            }}
            className="w-full"
          />
        </div>

        <div className="flex items-center font-semibold">
          <div className="mr-5 w-56 text-right">Company Email</div>
          <TextField
            type="text"
            name="main_email"
            value={formData.main_email}
            onChange={onChange}
            placeholder="example@email.com"
            className="w-full"
          />
        </div>

        <div className="flex items-start font-semibold">
          <div className="mr-5 w-56 text-right">Company Logo</div>
          <label className="w-full h-[200px] border border-[#EA33C0] rounded-lg flex flex-col items-center justify-center cursor-pointer bg-gray-50 hover:bg-gray-200 transition relative overflow-hidden">
            {formData.previewLogo ? (
              <img
                src={formData.previewLogo}
                alt="Preview"
                className="object-contain max-w-full max-h-full"
              />
            ) : (
              <>
                <div className="text-3xl text-gray-400">+</div>
                <p className="text-gray-600 text-sm text-center mt-2">
                  Add Image
                  <br />
                  (PNG, JPG or JPEG)
                </p>
              </>
            )}
            <input
              type="file"
              accept="image/png, image/jpeg, image/jpg"
              onChange={handleImageChange}
              className="hidden"
            />
          </label>
        </div>

        <div className="space-y-4">
          <div className="flex items-center font-semibold">
            <div className="mr-5 w-56 text-right">Shipping Address</div>
            <TextField
              type="text"
              name="shipping.address_line_1"
              value={formData.shipping.address_line_1}
              onChange={onChange}
              placeholder="Address"
              className="w-full"
            />
          </div>

          <div className="flex items-center font-semibold">
            <div className="mr-5 w-56"></div>
            <TextField
              type="text"
              name="shipping.address_line_2"
              value={formData.shipping.address_line_2}
              onChange={onChange}
              placeholder="Address 2"
              className="w-full"
            />
          </div>

          <div className="flex items-center font-semibold">
            <div className="mr-7 w-40"></div>
            <div className="flex">
              <TextField
                type="text"
                name="shipping.city"
                value={formData.shipping.city}
                onChange={onChange}
                placeholder="City"
                className="w-full mr-3"
              />
              <TextField
                type="text"
                name="shipping.state"
                value={formData.shipping.state}
                onChange={onChange}
                placeholder="State/Province"
                className="w-full"
              />
            </div>
          </div>

          <div className="flex items-center font-semibold">
            <div className="mr-7 w-40"></div>
            <div className="flex">
              <TextField
                type="text"
                name="shipping.country"
                value={formData.shipping.country}
                onChange={onChange}
                placeholder="Country"
                className="w-full mr-3"
              />
              <TextField
                type="text"
                name="shipping.zip_postal"
                value={formData.shipping.zip_postal}
                onChange={onChange}
                placeholder="Postal Code"
                className="w-full"
              />
            </div>
          </div>
        </div>

        <div className="flex">
          <div className="mr-5 w-40 text-right font-semibold">
            Billing Address
          </div>
          <Checkbox
            checked={formData.isSameAddress}
            onChange={(newValue) => {
              setFormData({
                ...formData,
                isSameAddress: newValue,
              });
            }}
          />
          <span className="ml-2">Same as shipping address</span>
        </div>

        {!formData.isSameAddress && (
          <div className="space-y-4">
            <div className="flex items-center font-semibold">
              <div className="mr-5 w-56 text-right"></div>
              <TextField
                type="text"
                name="billing.address_line_1"
                value={formData.billing.address_line_1}
                onChange={onChange}
                placeholder="Address"
                className="w-full"
              />
            </div>

            <div className="flex items-center font-semibold">
              <div className="mr-5 w-56"></div>
              <TextField
                type="text"
                name="billing.address_line_2"
                value={formData.billing.address_line_2}
                onChange={onChange}
                placeholder="Address 2"
                className="w-full"
              />
            </div>

            <div className="flex items-center font-semibold">
              <div className="mr-7 w-40"></div>
              <div className="flex">
                <TextField
                  type="text"
                  name="billing.city"
                  value={formData.billing.city}
                  onChange={onChange}
                  placeholder="City"
                  className="w-full mr-3"
                />
                <TextField
                  type="text"
                  name="billing.state"
                  value={formData.billing.state}
                  onChange={onChange}
                  placeholder="State/Province"
                  className="w-full"
                />
              </div>
            </div>

            <div className="flex items-center font-semibold">
              <div className="mr-7 w-40"></div>
              <div className="flex">
                <TextField
                  type="text"
                  name="billing.country"
                  value={formData.billing.country}
                  onChange={onChange}
                  placeholder="Country"
                  className="w-full mr-3"
                />
                <TextField
                  type="text"
                  name="billing.zip_postal"
                  value={formData.billing.zip_postal}
                  onChange={onChange}
                  placeholder="Postal Code"
                  className="w-full"
                />
              </div>
            </div>
          </div>
        )}

        <div className="pt-4 text-center flex justify-center">
          <Button type="primary" onClick={handleSubmit} isLoading={isLoading}>
            Save Info
          </Button>
        </div>
      </form>
    </div>
  );
};

export default StepTwoMediaAgencyOnboarding;
